import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {LoadingButton} from "@mui/lab";
import {Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {RichTextField} from "../../common/newRichText";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../common/uiElements/SurveyReviewStateElement";
import type {SurveySubmissionReview} from "./useSurveySubmissionReview";

interface SurveySubmissionReviewPanelProps {
    submission: AggregationSurveySubmissionListItem;
    review: SurveySubmissionReview;
    readOnly: boolean;
    /** Whether the host has a next open reply to move on to - only changes the resolve button's wording. */
    hasNextOpen: boolean;
}

const REVIEW_NOTES_ROWS = 8;

/** The review side of a reply: its state, who resolved it, the notes, Save and the one-click resolve. */
const SurveySubmissionReviewPanel = ({submission, review, readOnly, hasNextOpen}: SurveySubmissionReviewPanelProps) => {
    const {t} = useLocalize();

    return (
        <Stack gap={2.5}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                justifyContent={'space-between'}
                alignItems={{xs: 'stretch', sm: 'center'}}
                gap={1.5}
            >
                <Stack gap={0.25}>
                    <Typography variant={'h6'}>
                        {t('Status')}
                    </Typography>

                    {!!submission.reviewedAt && (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('Resolved at')}: {formatDateDMYHM(submission.reviewedAt)}
                            {submission.reviewedByName
                                ? ` · ${t('Resolved by')}: ${submission.reviewedByName}`
                                : ''}
                        </Typography>
                    )}
                </Stack>

                <NewStatusPicker<SurveyReviewState>
                    value={review.reviewState}
                    onChange={review.setReviewState}
                    disabled={readOnly}
                    ariaLabel={t('Review state')}
                    options={[
                        {
                            value: SurveyReviewState.OPEN,
                            label: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.OPEN])
                        },
                        {
                            value: SurveyReviewState.REVIEWED,
                            label: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.REVIEWED])
                        }
                    ]}
                    sx={{
                        width: {xs: '100%', sm: 'auto'},
                        '& .MuiToggleButton-root': {
                            flex: {xs: 1, sm: '0 0 auto'},
                        },
                    }}
                />
            </Stack>

            <Stack gap={0.75}>
                <Typography variant={'body1'} color={'text.secondary'}>
                    {t('Notes')}
                </Typography>

                <RichTextField
                    value={review.reviewNotes}
                    onChange={review.setReviewNotes}
                    placeholder={t('Notes')}
                    disabled={readOnly}
                    minRows={REVIEW_NOTES_ROWS}
                />
            </Stack>

            <Stack direction={'row'} justifyContent={'flex-end'} flexWrap={'wrap'} gap={1}>
                <LoadingButton
                    id={'btn-save-review'}
                    variant={review.canApprove ? 'outlined' : 'contained'}
                    onClick={review.save}
                    loading={review.saving}
                    disabled={!review.dirty || readOnly || review.approving}
                >
                    {t('Save')}
                </LoadingButton>

                {review.canApprove && (
                    <LoadingButton
                        id={'btn-mark-reviewed'}
                        variant={'contained'}
                        color={'success'}
                        startIcon={<CheckCircleOutlineIcon/>}
                        onClick={review.markReviewed}
                        loading={review.approving}
                        disabled={review.saving}
                    >
                        {hasNextOpen ? t('Mark as resolved and next') : t('Mark as resolved')}
                    </LoadingButton>
                )}
            </Stack>
        </Stack>
    );
};

export default SurveySubmissionReviewPanel;
